import { sendEduEvents } from '@/api/edu';
import type { EduEvent } from '@/api/types';
import { instrumentationDisabled } from './eduSettings';

/**
 * `edu.*` instrumentation client (F-ED-01, spec §2.3). A tiny batching singleton — NOT React state —
 * so any module can `track()` without prop-drilling and the flush survives route changes.
 *
 * Contract: fire-and-forget. `track()` is always synchronous and side-effect-free from the caller's
 * view; it NEVER throws, NEVER blocks, NEVER breaks a user action. Events flush on an interval, when
 * the batch fills, and on page-hide/unload (keepalive fetch — see api/edu.ts). Failures are dropped,
 * not retried. Props are enums / ids / durations only — NO free-text, NO PII (F-ED-05); a sanitiser
 * strips anything that is not a string/number/boolean as a defence-in-depth backstop.
 */

/** Load-bearing event names (dot-namespaced). Only names actually emitted live here. */
export const EDU = {
  sessionStarted: 'edu.session.started',
  tourStarted: 'edu.tour.started',
  tourStepViewed: 'edu.tour.step_viewed',
  tourCompleted: 'edu.tour.completed',
  tourSkipped: 'edu.tour.skipped',
  flowFirstCreated: 'edu.flow.first_created',
  flowFirstPublished: 'edu.flow.first_published',
  fieldHelpOpened: 'edu.field_help.opened',
  firstRunOffered: 'edu.first_run.offered',
  firstRunAccepted: 'edu.first_run.accepted',
  firstRunDismissed: 'edu.first_run.dismissed',
} as const;

export type EduEventProps = Record<string, string | number | boolean>;

const FLUSH_INTERVAL_MS = 8_000;
const MAX_BATCH = 20;
const MILESTONE_PREFIX = 'ol.edu.ms.';

let queue: EduEvent[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let lifecycleBound = false;
let sessionStarted = false;

/** Keep only primitive prop values — a backstop against ever shipping an object / free-text / PII. */
function sanitizeProps(props?: EduEventProps): EduEventProps | undefined {
  if (!props) return undefined;
  const out: EduEventProps = {};
  for (const [k, v] of Object.entries(props)) {
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') out[k] = v;
  }
  return Object.keys(out).length ? out : undefined;
}

function ensureLifecycle(): void {
  if (lifecycleBound) return;
  lifecycleBound = true;
  if (timer === null) timer = setInterval(flush, FLUSH_INTERVAL_MS);
  if (typeof window !== 'undefined') {
    // pagehide covers bfcache + real unload; visibilitychange→hidden covers tab-away / mobile.
    window.addEventListener('pagehide', flush);
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
  }
}

/** Drain the queue to the sink. Async, swallow-all — the caller never awaits and never sees a throw. */
export function flush(): void {
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];
  // Dropped on failure by design (fire-and-forget); never re-queued (no retry storms, no growth).
  void sendEduEvents(batch);
}

/** Enqueue one event. No-op when instrumentation is disabled (opt-out / env). Never throws. */
export function track(type: string, props?: EduEventProps): void {
  try {
    if (instrumentationDisabled()) return;
    ensureLifecycle();
    queue.push({ type, occurredAt: new Date().toISOString(), props: sanitizeProps(props) });
    if (queue.length >= MAX_BATCH) flush();
  } catch {
    /* instrumentation must never break a user action */
  }
}

/** Fire `edu.session.started` exactly once per SPA session (StrictMode-safe via a module guard). */
export function trackSessionStart(): void {
  if (sessionStarted) return;
  sessionStarted = true;
  track(EDU.sessionStarted);
}

/**
 * Fire a milestone event only the FIRST time it ever happens for this browser/tenant (TTFW
 * semantics — `edu.flow.first_created`, `edu.flow.first_published`). Persisted in localStorage so
 * "first" means first-ever, not first-this-session. Storage-safe.
 */
export function trackMilestoneOnce(type: string, key: string, props?: EduEventProps): void {
  const k = MILESTONE_PREFIX + key;
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem(k)) return;
    if (typeof localStorage !== 'undefined') localStorage.setItem(k, new Date().toISOString());
  } catch {
    /* storage unavailable — still emit once per session at least */
  }
  track(type, props);
}
